import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import type { FinanceAutomationCenter } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

export function FinanceAutomationPage() {
  const [center, setCenter] = useState<FinanceAutomationCenter | null>(null);
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const recentRuns = center?.recentRuns ?? [];
  const { rows: paginatedRuns, tablePaginationProps: runsPaginationProps } = useClientPaginatedRows(recentRuns);

  const load = () => financeApi.automationCenter().then(setCenter).catch((e) => showApiError(e, 'Failed to load automation'));

  useEffect(() => {
    load();
  }, []);

  const runJob = (jobCode: string) => {
    setRunningJob(jobCode);
    financeApi
      .runAutomationJob(jobCode)
      .then(() => load())
      .catch((e) => showApiError(e, 'Job run failed'))
      .finally(() => setRunningJob(null));
  };

  return (
    <div>
      <PageHeader
        title="Financial automation center"
        description="Recurring journals, revenue recognition, dunning, and depreciation — run manually or on schedule"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Automation' },
        ]}
      />

      <div className="grid gap-4 md:grid-cols-2 mb-6">
        {center?.jobs.map((job) => (
          <div key={job.jobCode} className="card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold">{job.jobName}</h3>
                <p className="text-sm text-slate-600 mt-1">{job.description}</p>
                <p className="text-xs text-slate-500 mt-2">{job.scheduleHint}</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={!job.runnable || runningJob === job.jobCode}
                onClick={() => runJob(job.jobCode)}
              >
                {runningJob === job.jobCode ? 'Running…' : 'Run now'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-4">
        <h3 className="font-semibold mb-3">Recent runs</h3>
        {!recentRuns.length ? (
          <p className="text-sm text-slate-500">No automation runs yet.</p>
        ) : (
          <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pb-2">Job</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Processed</th>
                <th className="pb-2">When</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRuns.map((run) => (
                <tr key={run.id} className="border-t">
                  <td className="py-2">{run.jobName}</td>
                  <td className="py-2">{run.statusCode}</td>
                  <td className="py-2">{run.processedCount}</td>
                  <td className="py-2">{new Date(run.runAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...runsPaginationProps} hasRows={recentRuns.length > 0} />
          </>
        )}
      </div>
    </div>
  );
}
